/// <summary>
/// 部署設定
/// ※メンバ変数の頭文字は小文字にしておくこと（キャメルケース）
/// </summary>

// 部署設定（データベース）
export class DivisionDBData {
    public divisionID: number;      // 部署ID
    public divisionName: string;    // 部署名
    public divisionNamePath: string;    // 部署名パス
    public divisionUINamePath: string // UI用の部署パス
    public parentID: number;        // 親ID
    public displayOrder: number;    // 表示順序

    constructor() {
        this.divisionID = 0;
        this.divisionName = "";
        this.divisionNamePath = "";
        this.divisionUINamePath = "";
        this.parentID = 0;
        this.displayOrder = 0;
    }

    public equalsObject(_obj: DivisionDBData): boolean {
        if (this.divisionID != _obj.divisionID) return false;
        if (this.divisionName != _obj.divisionName) return false;
        if (this.divisionNamePath != _obj.divisionNamePath) return false;
        if (this.divisionUINamePath != _obj.divisionUINamePath) return false;
        if (this.displayOrder != _obj.displayOrder) return false;
        if (this.parentID != _obj.parentID) return false;
        return true;
    }

    public equalsParent(_obj: DivisionDBData): boolean {
        if (this.parentID != _obj.parentID) return false;
        return true;
    }
}

// 部署毎の端末数（データベース）
export class DivisionAgentCountDBData {
    public divisionID: number;      // 部署ID
    public agentCount: number;      // 端末数

    constructor() {
        this.divisionID = 0;
        this.agentCount = 0;
    }
}

// 部署設定（定義値）
export class DivisionDef {
    public readonly DIVISION_NAME_MAX_LENGTH = 39;            // 部署名（最大文字）
    public readonly DIVISION_MAX_LENGTH = 32;                      // 最大階層
    public readonly ROOT_DIVISION_NAME = "ネットワーク全体";       // ルート部署名
    public readonly INVALID_DIVISIONNAME_CHAR = "\\/:*?\"<>|,.-";  // 部署名に使えない文字（正規表現）
    public readonly INVALID_DIVISIONNAME_CHAR_DISP = "\\ / : * ? \" < > | , . -";   // 部署名に使えない文字（表示用）
}

// 部署設定（UI用に扱いやすいようにしたデータクラス）
export class DivisionData {
    public divisionId: number;      // 部署ID
    public divisionName: string;    // 部署名
    public divisionNamePath: string;  // 部署名パス
    public divisionUINamePath: string; // UI用部署パス名
    public arChildren: Array<DivisionData> = new Array<DivisionData>(); // 子部署

    // オプション変数
    public alertAgentCount: number; // アラート端末数
    public checkState: number; // チェック状態

    static NEW_DIVISION_ID = -1;                                   // 新規部署の仮ID

    constructor() {
        this.divisionId = 0;
        this.divisionName = "";
        this.divisionNamePath = "";
        this.divisionUINamePath = "";
        this.alertAgentCount = 0;
        this.checkState = 0;
    }

    /// <summary>
    /// 初期化
    /// </summary>
    public SetDefault(): void {
        this.divisionId = 0;
        this.divisionName = "";
        this.divisionNamePath = "";
        this.divisionUINamePath = "";
        this.alertAgentCount = 0;
        this.checkState = 0;
        this.arChildren = new Array<DivisionData>();
    }

    /// <summary>
    /// データベースの情報をデータクラスに変換します
    /// @param [in] arDivList : データベースのデータ
    /// @param [in] arAlertAgentCount : 部署と所属するアラート端末数（アラート状態を部署ツリーに反映した場合）
    /// </summary>
    public Load(
        arDivList: Array<DivisionDBData>,
        arAlertAgentCount: Array<DivisionAgentCountDBData> | undefined = undefined
    ): void {
        // 各編集を初期化
        this.SetDefault();

        // アラート端末数のマップ（キーは部署ID）
        const alertMap = new Map<number, number>();
        if (undefined != arAlertAgentCount) {
            arAlertAgentCount.forEach(x => alertMap.set(x.divisionID, x.agentCount));
        }

        // ここで表示順にソートしておくことで、下では順序を意識せずに追加できる
        arDivList.sort(function(a, b) {
            // 大文字、小文字は区別する
            if (a.parentID < b.parentID) {
                return -1;
            }
            if (a.parentID > b.parentID) {
                return 1;
            }
            if (a.displayOrder < b.displayOrder) {
                return -1;
            }
            if (a.displayOrder > b.displayOrder) {
                return 1;
            }

            return 0;
        });

        const divMap = new Map<number, DivisionData>();         // 部署情報のインスタンスマップ（キーは部署ID）
        const arCacheItem = new Array<DivisionDBData>();        // 一巡目では親が見つからなかった部署

        // まずは全部署情報を作成する
        const nCount = arDivList.length;
        for (let i = 0; i < nCount; i++) {

            let item = new DivisionData();
            if (1 == arDivList[i].divisionID) {
                // ネットワーク全体の場合はthisを代入
                /* eslint @typescript-eslint/no-this-alias: 0 */
                item = this;
            }

            item.divisionId = arDivList[i].divisionID;
            item.divisionName = arDivList[i].divisionName;
            item.divisionNamePath = arDivList[i].divisionNamePath;
            item.divisionUINamePath = arDivList[i].divisionUINamePath;
            divMap.set(arDivList[i].divisionID, item);

            // 所属アラート端末数
            const alertCount = alertMap.get(item.divisionId);
            if (undefined != alertCount) {
                item.alertAgentCount = alertCount;
            }

            if (1 == arDivList[i].divisionID) {
                // ネットワーク全体の場合は親はいない
                continue;
            }

            // 部署の親子関係を紐づけ
            const parent = divMap.get(arDivList[i].parentID);
            if (undefined == parent) {
                // まだ親が作成されていないので、キャッシュしておく
                arCacheItem.push(arDivList[i]);
                continue;
            }
            parent.arChildren.push(item);
        }

        // ここまでくれば全部署情報が作成されているので、
        // 一巡目で親が見つからなかった部署も、見つかるはず
        const nCacheCount = arCacheItem.length;
        for (let i = 0; i < nCacheCount; i++) {
            const divItem = divMap.get(arCacheItem[i].divisionID);
            if (undefined == divItem) {
                continue;
            }

            const parent = divMap.get(arCacheItem[i].parentID);
            if (undefined == parent) {
                // ここでも作成されていない場合はあきらめる
                continue;
            }
            parent.arChildren.push(divItem);
        }
    }

    /// <summary>
    /// データクラスをデータベースの情報に変換します
    /// @param [out] arDivList : データベース情報
    /// </summary>
    public Save(arDivList: Array<DivisionDBData>): void {
        const item = new DivisionDBData();
        item.divisionID = this.divisionId;
        item.divisionName = this.divisionName;
        item.divisionNamePath = this.divisionNamePath;
        item.divisionUINamePath = this.divisionUINamePath;
        item.parentID = 0;
        item.displayOrder = 1;
        arDivList.push(item);

        this.SaveChildren(arDivList);
    }

    /// <summary>
    /// データクラスをデータベースの情報に変換します（子部署）
    /// @param [out] arDivList : データベース情報
    /// </summary>
    public SaveChildren(arDivList: Array<DivisionDBData>): void {
        for (let i = 0; i < this.arChildren.length; i++) {
            const item = new DivisionDBData();
            item.divisionID = this.arChildren[i].divisionId;
            item.divisionName = this.arChildren[i].divisionName;
            item.divisionNamePath = this.arChildren[i].divisionNamePath;
            item.divisionUINamePath = this.divisionUINamePath;
            item.parentID = this.divisionId;
            item.displayOrder = i + 1;

            arDivList.push(item);

            this.arChildren[i].SaveChildren(arDivList);
        }
    }

    /**
     * 指定された部署IDの部署情報を返します
     * @param[in] nDivisionID : 部署ID
     * @return undefined:見つからなかった undefined以外：部署ID
     */
    public GetDivisionData(nDivisionID: number): DivisionData | undefined {
        if (this.divisionId == nDivisionID) {
            return this;
        }

        for (let i = 0; i < this.arChildren.length; i++) {
            const ret = this.arChildren[i].GetDivisionData(nDivisionID);
            if (undefined != ret) {
                return ret;
            }
        }

        return undefined;
    }

    /**
     * 指定された部署IDの親部署情報を返します
     * @param[in] nDivisionID : 部署ID
     * @return undefined:見つからなかった undefined以外：部署ID
     */
    public GetParentDivisionData(nDivisionID: number): DivisionData | undefined {
        if (this.divisionId == nDivisionID) {
            // 自分は親ではない
            return undefined;
        }

        for (let i = 0; i < this.arChildren.length; i++) {
            if (this.arChildren[i].divisionId == nDivisionID) {
                // 自分の子
                return this;
            }

            const ret = this.arChildren[i].GetParentDivisionData(nDivisionID);
            if (undefined != ret) {
                // 孫以上
                return ret;
            }
        }

        return undefined;
    }

    /**
     * 指定された部署IDの子部署を削除します
     * @param[in] nDivisionID : 部署ID
     * @return true : 削除        false : 対象が見つからない
     */
    public DeleteChildDivisionData(nDivisionID: number): boolean {
        for (let i = 0; i < this.arChildren.length; i++) {
            if (this.arChildren[i].divisionId == nDivisionID) {
                this.arChildren.splice(i, 1);
                return true;
            }

            const ret = this.arChildren[i].DeleteChildDivisionData(nDivisionID);
            if (true == ret) {
                return ret;
            }
        }

        return false;
    }

    /**
     * 部署ID一覧を取得します
     * @param[out] arDivisionID : 部署ID一覧
     */
    public GetAllDivisionID(arDivisionID: Array<number>): void {
        arDivisionID.push(this.divisionId);

        for (let i = 0; i < this.arChildren.length; i++) {
            this.arChildren[i].GetAllDivisionID(arDivisionID);
        }
    }

    /**
     * 階層を取得します
     * @param[in] nDivisionID : 部署ID
     * @param[out] nResult : 結果（階層）
     */
    public GetHierarchy(nDivisionID: number): number {
        if (this.divisionId == nDivisionID) {
            return 1;
        }

        for (let i = 0; i < this.arChildren.length; i++) {
            if (this.arChildren[i].divisionId == nDivisionID) {
                // 自分の子
                return 2;
            }

            const ret = this.arChildren[i].GetHierarchy(nDivisionID);
            if (0 != ret) {
                // 孫以上
                return ret + 1;
            }
        }

        return 0;
    }

    /**
     * 配下部署が何階層あるかを取得します
     * @return : 結果（階層）※自分を含めての値
     */
    public GetChildrenHierarchy(): number {
        let nChild = 0;

        for (let i = 0; i < this.arChildren.length; i++) {
            const nTemp = this.arChildren[i].GetChildrenHierarchy();

            if (nChild < nTemp) {
                nChild = nTemp;
            }
        }

        // 最後に自分を含める
        return nChild + 1;
    }

    /**
     * UI用の部署パスを検索して部署IDを検索する
     * @return : 見つかった場合はその部署ID情報を、見つからなかった場合は null を返します。
     */
    public findDivisionIDFromUINamePath(divisionUINamePath: string): DivisionData | null {

        if (this.divisionUINamePath === divisionUINamePath) {
            return this;
        }

        // 見つからない場合は子を再帰的に探す
        for (let i = 0; i < this.arChildren.length; ++i) {

            const found = this.arChildren[i].findDivisionIDFromUINamePath(divisionUINamePath);

            if (null !== found) {
                return found;
            }
        }

        return null;
    }

    /**
     * 子部署のチェック状態を変更する
     * @param[in] bCheck : チェック状態
     */
    public CheckChildDivision(bCheck: boolean): void {
        for (let i = 0; i < this.arChildren.length; i++) {
            this.arChildren[i].checkState = bCheck ? 1 : 0;

            this.arChildren[i].CheckChildDivision(bCheck);
        }
    }

    /**
     * チェック状態を変更する
     * @param[in] divisionIDs : チェックがオンの部署ID
     */
    public SetCheckAllDivision(divisionIDs: Array<number>): void {
        if (divisionIDs.includes(this.divisionId)) {
            this.checkState = 1;
        }
        else {
            this.checkState = 0;
        }

        for (let i = 0; i < this.arChildren.length; i++) {
            this.arChildren[i].SetCheckAllDivision(divisionIDs);
        }
    }

    /**
     * チェック状態を取得する
     * @param[out] divisionIDs : isCheckでしてした状態の部署ID
     * @param[in] isCheck: チェックオンの部署かオフの部署か
     */
    public GetCheckAllDivision(divisionIDs: Array<number>, isCheck = true): void {
        if (isCheck) {
            if (1 == this.checkState) {
                divisionIDs.push(this.divisionId);
            }
        }
        else {
            if (0 == this.checkState) {
                divisionIDs.push(this.divisionId);
            }
        }

        for (let i = 0; i < this.arChildren.length; i++) {
            this.arChildren[i].GetCheckAllDivision(divisionIDs, isCheck);
        }
    }

    /**
     * 全ての部署のチェックを変更する
     * @param[in] bCheck : チェック状態
     * @param[in] bTriStateMode : 現在と異なっていた場合、3ステートにするかどうか
     */
    public SetCheckDivision(bCheck: boolean, bTriStateMode: boolean = false): void {
        if (!bTriStateMode) {
            this.checkState = bCheck ? 1 : 0;
        }
        else {
            // 3ステート判定
            this.SetTriState(bCheck ? 1 : 0);
        }
        
        for (let i = 0; i < this.arChildren.length; i++) {
            this.arChildren[i].SetCheckDivision(bCheck, bTriStateMode);
        }
    }

    /**
    *  チェックされている部署のうち、最上位の部署IDを取得
    * @param[out] divisionIDs : チェックされている部署のうち最上位の部署ID
    * @param[out] triStateDivisionIDs : 3ステートの部署ID
    */
    public GetCheckParentDivision(divisionIDs: Array<number>, triStateDivisionIDs: Array<number>): void {
        if (1 == this.checkState) {
            divisionIDs.push(this.divisionId);
            return;
        }
        if (2 == this.checkState) {
            triStateDivisionIDs.push(this.divisionId);
        }
        for (let i = 0; i < this.arChildren.length; i++) {
            this.arChildren[i].GetCheckParentDivision(divisionIDs, triStateDivisionIDs);
        }
    }

    /**
     * 親部署を指定し、その子部署までチェックを行う
     * @param[out] parentDivisionIDs : チェックオンの親部署ID
     * @param[in] bTriStateMode : 現在と異なっていた場合、3ステートにするかどうか
     */
    public SetCheckParentDivision(parentDivisionIDs: Array<number>, bTriStateMode: boolean): void {
        if (parentDivisionIDs.includes(this.divisionId)) {
            // 子部署も含めてチェックオン
            this.SetCheckDivision(true, bTriStateMode);
        }
        else {
            // チェックオフ
            if (bTriStateMode) {
                this.SetTriState(0);
            }
            else {
                this.checkState = 0;
            }

            // 子部署に親部署がないかさらに判定する
            for (let i = 0; i < this.arChildren.length; i++) {
                this.arChildren[i].SetCheckParentDivision(parentDivisionIDs, bTriStateMode);
            }
        }
    }

    /**
     * 現在のチェック状態から受け取ったチェック状態をマージする
     * @param[in] nCheck : チェック状態
     */
    public SetTriState(nCheck: number): void {
        if (0 == this.checkState) {
            if (1 == nCheck) {
                // チェックオフ→チェックオンは3ステート
                this.checkState = 2;
            }
        }
        else if (1 == this.checkState) {
            if (0 == nCheck) {
                // チェックオン→チェックオフは3ステート
                this.checkState = 2;
            }
        }
        else {
            // すでに3ステート
        }
    }

    /**
     * 部署配下に所属しているか確認
     * @param[in] divisionData : この部署の配下に所属しているか確認
     * @return : true : 所属している    false : 所属していない
     */
    public isAffiliateDivision(divisionData: DivisionData): boolean {
        if (divisionData.divisionId == this.divisionId) {
            return true;
        }
        for (let i = 0; i < divisionData.arChildren.length; i++) {
            if (this.isAffiliateDivision(divisionData.arChildren[i])) {
                return true;
            }
        }

        return false;
    }

    /**
     * 閲覧可能な部署取得(private)
     * @param[out] divisionIDs : 閲覧可能な部署IDリスト
     * @param[in] managementDivisionIDs : 閲覧可能な部署IDリスト(各部署の先頭のIDのみ)
     */
    public GetEnableDivisionID(divisionIDs: Set<number>, managementDivisionIDs: Set<number>): void {
        if (managementDivisionIDs.has(this.divisionId)) {
            const ids = new Array<number>();
            this.GetAllDivisionID(ids);
            ids.forEach(id => divisionIDs.add(id));
            return;
        }

        for (const children of this.arChildren) {
            children.GetEnableDivisionID(divisionIDs, managementDivisionIDs);
        }
    }

    /**
     * 閲覧不可能な部署取得
     * @param[in] managementDivisionIDs : 閲覧可能な部署IDリスト(各部署の先頭のIDのみ)
     * @retrun 閲覧不可能な部署IDリスト
     */
    public GetDisableDivisionID(managementDivisionIDs: Set<number>): Set<number> {
        // 閲覧可能な部署ID取得
        const enableIds = new Set<number>();
        this.GetEnableDivisionID(enableIds, managementDivisionIDs);

        // すべての部署ID取得
        const allIDs = new Array<number>();
        this.GetAllDivisionID(allIDs);

        // 閲覧可能部署を抽出
        const disableIDs = new Set<number>();
        allIDs.forEach(id => {
            if (!enableIds.has(id)) {
                disableIDs.add(id);
            }
        });
        return disableIDs;
    }

    /**
     * 引数リストのdivisionIDsの値が一致する一番上の部署データを取得
     * @param[in] divisionIDs : 検索する部署IDリスト
     * @return 検索対象の内、一番上の部署データ
     */
    public GetTopLevelDivisionDBData(divisionIDs: Set<number>): DivisionData | null {
        if (divisionIDs.has(this.divisionId)) {
            return this;
        }

        for (const childDivisionData of this.arChildren) {
            const divisionData = childDivisionData.GetTopLevelDivisionDBData(divisionIDs);
            if (null != divisionData) {
                return divisionData;
            }
        }

        return null;
    }

    /**
     * 部署配下の全部署ID一覧を再帰的に取得します
     * @param[out] arDivisionID : 部署ID一覧（選択部署を含まない）
     */
    public GetSubDivisionIDs(arDivisionID: Set<number>) {
        this.arChildren.map(division => division.divisionId).forEach(id => arDivisionID.add(id));

        for (const division of this.arChildren) {
            division.GetSubDivisionIDs(arDivisionID);
        }
    }

    /**
     * ネットワーク全体をのぞいたフルパスの部署名に変換
     */
    public getDivisionUINamePath() :  string{

        if (this.divisionNamePath == "ネットワーク全体") {
            return this.divisionNamePath;
        }
        return this.divisionNamePath.replace("ネットワーク全体/", "");
    }
}

// 部署設定（UI用に扱いやすいようにしたデータクラス）
export class SelectedDivisionInfo {
    public divisionId = 1;                      // 部署ID
    public divisionName = "ネットワーク全体";   // 部署名
    public enableChildDivision = true;          // 配下部署表示
}
